import React, { useState, useEffect, useCallback } from 'react';
import Api from '../../api';
import { onError } from '../../libs/error-lib';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import { Box, Flex } from 'reflexbox';
import { Button, Form, HovereableFlex, RefreshIcon, Modal, SaveButton, FormLeyend, Tooltip } from 'boss-ui';
import SearchUsers from '../../components/Users/SearchUsers';
import FormInput from '../../components/FormInput';
import DefaultAdminTable from './DefaultEventAdminTable';

const MainContainer = styled(Flex)`
  padding: 30px 40px;
  width: 924px;
  height: 700px;
  flex-direction: column;
`;

const PartialFilterContainer = styled(Box)`
    width: 20%;
    margin-left: auto;
`;

const FilterContainer = styled(Flex)`
    margin-bottom: 20px;
    align-items: center;
    gap: 10px;
`;

const SearchContainer = styled(Box)`
    width: 60%;
`;

const RefreshIconContainer = styled(HovereableFlex)`
    width: 20px;
    margin-left: 10px;
    margin-bottom: 12px;
`;

const AddButton = styled(Button)`
    height: 38px;
    align-self: flex-end;
`;

const API = new Api();

export default function DefaultEventAdminsComponent() {
    const [isLoading, setIsLoading] = useState(false);
    const [defaultAdmins, setDefaultAdmins] = useState([]);
    const [filteredAdmins, setFilteredAdmins] = useState([]);
    const [searchString, setSearchString] = useState('');
    const [rowHoverId, setRowHoverId] = useState(null);
    const [addNewDefaultAdmin, setAddNewDefaultAdmin] = useState(false);

    const getDefaultAdmins = async () => {
        setIsLoading(true);
        try {
            const response = await API.get('events', '/admin/default-admins');
            const admins = response.users || [];
            setDefaultAdmins(admins);
            setFilteredAdmins(admins);
        } catch (error) {
            onError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const postNewDefaultAdmin = async (users) => {
        setIsLoading(true);
        try {
            await API.post('events', '/admin/default-admins', { body: { users } });
            const updatedAdmins = [...defaultAdmins, ...users];
            setDefaultAdmins(updatedAdmins);
            filterAdmins(searchString, updatedAdmins);
        } catch (error) {
            onError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const removeDefaultAdmin = async (user) => {
        setIsLoading(true);
        try {
            const response = await API.del('events', `/admin/default-admins/${user.userId}`);
            toast(response.message);
            const updatedAdmins = defaultAdmins.filter((u) => u.userId !== user.userId);
            setDefaultAdmins(updatedAdmins);
            filterAdmins(searchString, updatedAdmins);
        } catch (error) {
            onError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const addNewUsers = (users) => {
        postNewDefaultAdmin(users);
        setAddNewDefaultAdmin(false);
    };

    // Remove the useCallback and make it a simple function
    const filterAdmins = (query, adminsList = defaultAdmins) => {
        if (!query.trim()) {
            setFilteredAdmins(adminsList);
            return;
        }

        const filtered = adminsList.filter((admin) => {
            const searchQuery = query.toLowerCase();
            return (
                admin.displayName?.toLowerCase().includes(searchQuery) ||
                admin.email?.toLowerCase().includes(searchQuery)
            );
        });

        setFilteredAdmins(filtered);
    };

    // Update debouncedFilter
    const debouncedFilter = useCallback(
        debounce((query) => filterAdmins(query), 300),
        [defaultAdmins] // Only depend on defaultAdmins
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchString(value);
        debouncedFilter(value);
    };


    const clearSearch = () => {
        setSearchString('');
        setFilteredAdmins(defaultAdmins);
    };

    useEffect(() => {
        const fetchData = async () => {
            await getDefaultAdmins();
        }
        fetchData();
    }, []);

    // Simplify the useEffect
    useEffect(() => {
        filterAdmins(searchString);
    }, [defaultAdmins, searchString]); // This is fine now since filterAdmins is not memoized

    return (
        <>
            <Form noBorder>
                <MainContainer>
                    <FilterContainer>
                        <SearchContainer>
                            <FormLeyend>Find a Default Event Admin</FormLeyend>
                            <FormInput
                                value={searchString}
                                onChange={handleSearchChange}
                                placeholder="Search by name or email..."
                                height="34px"
                                search={searchString}
                                remove={searchString.length > 0 ? clearSearch : null}
                            />
                        </SearchContainer>
                        <PartialFilterContainer>
                            <AddButton onClick={() => setAddNewDefaultAdmin(true)}>
                                + Add
                            </AddButton>
                        </PartialFilterContainer>
                    </FilterContainer>
                    <RefreshIconContainer data-tip data-for="refresh">
                        <RefreshIcon
                            onClick={() => getDefaultAdmins()}
                            rotate={isLoading}
                            color="#898989"
                            hoverColor="#d0d0d0"
                        />
                        <Tooltip id="refresh" > Refresh list</Tooltip>
                    </RefreshIconContainer>
                    <DefaultAdminTable
                        isLoading={isLoading}
                        filteredAdmins={filteredAdmins}
                        rowHoverId={rowHoverId}
                        setRowHoverId={setRowHoverId}
                        removeDefaultAdmin={removeDefaultAdmin}
                        />
                </MainContainer>
            </Form>
            {addNewDefaultAdmin && (
                <Modal
                    onBackgroundClick={() => setAddNewDefaultAdmin(false)}
                    margin={'10vh auto'}
                >
                    <SearchUsers
                        selectable
                        loadingSubmit={isLoading}
                        onSelectedSubmit={addNewUsers}
                        submitSelectedButton={<SaveButton loading={isLoading} />}
                        enrolledUsers={defaultAdmins}
                        forceAdminRole={true}
                    />
                </Modal>
            )}
        </>
    );
}
